// ============================================================================
// ADVOCATE MASTER SCREEN — MSTR-ADV-001
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ColumnSelector, type ColumnDefinition } from 'components/common';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { useAdvocates } from 'hooks/api/useAdvocates';
import { advocateService } from 'api/services/advocateService';
import { countryService } from 'api/services/countryService';
import { stateProvinceService } from 'api/services/stateProvinceService';
import type { Advocate, AdvocateSearchRequest } from 'api/types/advocate.types';
import AdvocateFormModal from 'components/modals/modules/advocate-module/AdvocateFormModal';

type SelectOption = { value: string; label: string };

type ViewMode = 'list' | 'grid';

interface AdvocateMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const AdvocateMasterScreen: React.FC<AdvocateMasterScreenProps> = ({ tabId, moduleName }) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [queryParams, setQueryParams] = useState<AdvocateSearchRequest>({
    page: 1,
    pageSize: 25,
  });
  const { data, loading, pagination, refetch } = useAdvocates(queryParams);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [advocateCodeFilter, setAdvocateCodeFilter] = useState('');
  const [advocateNameFilter, setAdvocateNameFilter] = useState('');
  const [barRegFilter, setBarRegFilter] = useState('');
  const [specFilter, setSpecFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [countryIdFilter, setCountryIdFilter] = useState<string>('');
  const [stateProvinceIdFilter, setStateProvinceIdFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Country/state options for the filter cascade.
  const [countryFilterOptions, setCountryFilterOptions] = useState<SelectOption[]>([]);
  const [stateFilterOptions, setStateFilterOptions] = useState<SelectOption[]>([]);

  useEffect(() => {
    const t = setTimeout(() => {
      setQueryParams(prev => ({
        ...prev,
        searchTerm: searchQuery.trim() || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Paginated loader (API caps pageSize at 100; Country has 249 rows).
  const loadCountryFilterOptions = useCallback(async () => {
    try {
      const all: Array<{ id: string; countryCode: string; countryName: string }> = [];
      for (let page = 1; page <= 10; page++) {
        const res = await countryService.getAll({ page, pageSize: 100 });
        all.push(...res.data);
        if (!res.pagination.hasNextPage) break;
      }
      setCountryFilterOptions(all.map(c => ({
        value: c.id,
        label: `${c.countryName} (${c.countryCode})`,
      })));
    } catch {
      setCountryFilterOptions([]);
    }
  }, []);

  const loadStateFilterOptions = useCallback(async (countryId: string) => {
    if (!countryId) { setStateFilterOptions([]); return; }
    try {
      const all: Array<{ id: string; provinceCode: string; provinceName: string }> = [];
      for (let page = 1; page <= 10; page++) {
        const res = await stateProvinceService.getAll({ page, pageSize: 100, countryId });
        all.push(...res.data);
        if (!res.pagination.hasNextPage) break;
      }
      setStateFilterOptions(all.map(s => ({
        value: s.id,
        label: `${s.provinceName} (${s.provinceCode})`,
      })));
    } catch {
      setStateFilterOptions([]);
    }
  }, []);

  // Load countries on first filter-panel open (deferred — no cost until used).
  const didLoadCountryFilterRef = useRef(false);
  useEffect(() => {
    if (filterOpen && !didLoadCountryFilterRef.current) {
      didLoadCountryFilterRef.current = true;
      loadCountryFilterOptions();
    }
  }, [filterOpen, loadCountryFilterOptions]);

  const handleFilterCountryChange = useCallback((val: string) => {
    // Clear state if country cleared or changed.
    setCountryIdFilter(val);
    setStateProvinceIdFilter('');
    if (val) loadStateFilterOptions(val);
    else setStateFilterOptions([]);
  }, [loadStateFilterOptions]);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit' | 'copy' | 'view'>('create');
  const [selectedAdv, setSelectedAdv] = useState<Advocate | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find(a => a.advocateCode === recordCode);
        if (rec) setSelectedAdv(rec);
      }
      setFormMode(restoredMode);
      setFormModalOpen(true);
    },
    [data]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [advToDelete, setAdvToDelete] = useState<Advocate | null>(null);

  const handleAddNew = useCallback(() => {
    if (formModalOpen) {
      formModalMinimizeRef.current?.();
    }
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedAdv(null);
    setFormMode('create');
    setFormModalOpen(true);
  }, [formModalOpen]);

  const handleEdit = useCallback(
    (a: Advocate) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${a.advocateCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedAdv(a);
      setFormMode('edit');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  const handleView = useCallback(
    (a: Advocate) => {
      const modalId = tabId ? `${tabId}-view-modal-${a.advocateCode}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedAdv(a);
      setFormMode('view');
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal],
  );

  const handleCopy = useCallback(
    (a: Advocate) => {
      setSelectedAdv(a);
      setFormMode('copy');
      setFormModalOpen(true);
    },
    [],
  );

  const handleDeleteClick = useCallback((a: Advocate) => {
    setAdvToDelete(a);
    setDeleteModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!advToDelete) return;
    setIsDeleting(true);
    try {
      await advocateService.delete(advToDelete.id);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      notificationService.toast.success(`Advocate "${advToDelete.advocateCode}" deleted successfully.`);
      setAdvToDelete(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const msg = err?.response?.data?.message || 'An error occurred while deleting.';
      notificationService.toast.error(msg, 6000);
    }
  }, [advToDelete, refetch]);

  const handleSaved = useCallback(() => { refetch(); }, [refetch]);
  const handleRefresh = useCallback(() => { refetch(); }, [refetch]);

  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams(prev => ({
      ...prev,
      advocateCode: advocateCodeFilter.trim() || undefined,
      advocateName: advocateNameFilter.trim() || undefined,
      barCouncilRegNo: barRegFilter.trim() || undefined,
      specialisation: specFilter.trim() || undefined,
      city: cityFilter.trim() || undefined,
      countryId: countryIdFilter || undefined,
      stateProvinceId: stateProvinceIdFilter || undefined,
      statusCode: statusFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [advocateCodeFilter, advocateNameFilter, barRegFilter, specFilter, cityFilter, countryIdFilter, stateProvinceIdFilter, statusFilter]);

  const handleClearFilters = useCallback(() => {
    setAdvocateCodeFilter('');
    setAdvocateNameFilter('');
    setBarRegFilter('');
    setSpecFilter('');
    setCityFilter('');
    setCountryIdFilter('');
    setStateProvinceIdFilter('');
    setStateFilterOptions([]);
    setStatusFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.advocateCode) count++;
    if (queryParams.advocateName) count++;
    if (queryParams.barCouncilRegNo) count++;
    if (queryParams.specialisation) count++;
    if (queryParams.city) count++;
    if (queryParams.countryId) count++;
    if (queryParams.stateProvinceId) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams]);

  const columns: SyutGridColumn<Advocate>[] = useMemo(() => [
    { id: 'advocateCode', header: 'Code', accessorKey: 'advocateCode', size: 120, enableSorting: true },
    { id: 'advocateName', header: 'Name', accessorKey: 'advocateName', size: 240, enableSorting: true, flex: true },
    { id: 'barCouncilRegNo', header: 'Bar Reg #', accessorKey: 'barCouncilRegNo', size: 160,
      cell: ({ row }) => row.original.barCouncilRegNo || '-' },
    { id: 'specialisation', header: 'Specialisation', accessorKey: 'specialisation', size: 180,
      cell: ({ row }) => row.original.specialisation || '-' },
    { id: 'city', header: 'City', accessorKey: 'city', size: 130,
      cell: ({ row }) => row.original.city || '-' },
    { id: 'stateProvinceName', header: 'State', accessorKey: 'stateProvinceName', size: 140,
      cell: ({ row }) => row.original.stateProvinceName || '-' },
    { id: 'countryCode', header: 'Country', accessorKey: 'countryCode', size: 90,
      cell: ({ row }) => row.original.countryCode || '-' },
    { id: 'phoneNumber', header: 'Phone', accessorKey: 'phoneNumber', size: 140,
      cell: ({ row }) => row.original.phoneNumber || '-' },
    {
      id: 'statusCode',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 110,
      enableSorting: true,
      cell: ({ row }) => {
        const c = row.original;
        const code = c.deletedAt ? 'DELETED' : (c.statusCode || 'ACTIVE');
        let color = '#6b7280';
        if (code === 'ACTIVE') color = '#16a34a';
        else if (code === 'DELETED') color = '#dc2626';
        return (
          <span
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color,
              padding: '2px 8px',
              border: `1px solid ${color}`,
              borderRadius: '999px',
            }}
            title={c.deletedAt ? `Deleted at ${new Date(c.deletedAt).toLocaleString()}` : undefined}
          >
            {code}
          </span>
        );
      },
    },
  ], []);

  // Column visibility (driven by ColumnSelector toolbar control)
  const columnDefinitions: ColumnDefinition[] = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id as string,
        label: typeof col.header === 'string' ? (col.header as string) : (col.id as string),
        defaultVisible: true,
      })),
    [columns],
  );
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id),
  );
  const handleApplyColumns = useCallback((cols: string[]) => setVisibleColumns(cols), []);
  const handleResetColumns = useCallback(() => {
    setVisibleColumns(columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id));
  }, [columnDefinitions]);

  const getRowActions = useCallback((a: Advocate): ActionMenuItem[] => [
    { id: 'view', label: 'View Details', icon: <IconSystem.eye size={16} />, onClick: () => handleView(a) },
    { id: 'edit', label: 'Edit', icon: <IconSystem.edit size={16} />, onClick: () => handleEdit(a) },
    { id: 'copy', label: 'Copy', icon: <IconSystem.copy size={16} />, onClick: () => handleCopy(a) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => handleDeleteClick(a), variant: 'danger' },
  ], [handleEdit, handleView, handleCopy, handleDeleteClick]);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add', icon: <IconSystem.add size={18} />, label: 'Add', tooltip: 'Add New Advocate', onClick: handleAddNew, variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', tooltip: 'Refresh', onClick: handleRefresh, variant: 'icon' },
  ], [handleAddNew, handleRefresh]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Advocate Master"
        description="External lawyers empanelled with the NBFC. Bar council reg#, specialisation, empanelment dates, contact, and address."
        icon={<IconSystem.user size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, bar reg#, city, state...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
                visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={columns}
                enableSorting
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              visibleColumns={visibleColumns}
                rowActions={getRowActions}
                isLoading={loading}
                emptyMessage="No advocates found"
              />
            )}

            {viewMode === 'grid' && (
              <div className="syut-grid-component" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '1rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.map((a) => (
                    <div
                      key={String(a.id)}
                      style={{
                        position: 'relative',
                        border: '1px solid var(--border-color)',
                        borderRadius: '8px',
                        padding: '1rem',
                        background: 'var(--bg-primary)',
                        cursor: 'pointer',
                      }}
                      onClick={() => handleEdit(a)}
                    >
                      {/* Action menu pinned to the card's top-right; stop click
                          propagation so opening the menu doesn't also fire the
                          card's onClick. */}
                      <div
                        style={{ position: 'absolute', top: 6, right: 6 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionMenu items={getRowActions(a)} />
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 600, paddingRight: '1.5rem' }}>
                        {a.advocateCode}
                      </div>
                      <div style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem', color: 'var(--text-primary)' }}>
                        {a.advocateName}
                      </div>
                      {a.specialisation && (
                        <div style={{ fontSize: '0.8125rem', marginTop: '0.25rem', color: 'var(--text-secondary)' }}>
                          {a.specialisation}
                        </div>
                      )}
                      {(a.city || a.stateProvinceName) && (
                        <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: 'var(--text-muted)' }}>
                          {[a.city, a.stateProvinceName].filter(Boolean).join(', ')}
                        </div>
                      )}
                      <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', color: a.statusCode === 'ACTIVE' ? '#16a34a' : '#6b7280' }}>
                        {a.statusCode || 'ACTIVE'}
                      </div>
                    </div>
                  ))}
                </div>
                {data.length > 0 && (
                  <div className="pagination-footer">
                    <div className="pagination-info">
                      Showing {data.length} of {pagination.totalItems} items
                    </div>
                    <div className="pagination-controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <select
                        value={queryParams.pageSize ?? 25}
                        onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                        style={{ padding: '0.375rem 0.5rem', borderRadius: '0.375rem', border: '1px solid var(--border-color)', fontSize: '0.85rem', marginRight: '0.5rem' }}
                      >
                        {[10, 25, 50, 100].map((size) => (
                          <option key={size} value={size}>{size} / page</option>
                        ))}
                      </select>
                      <button className="page-btn" onClick={() => handlePageChange(0)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                        <IconSystem.chevronLeft size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange((queryParams.page ?? 1) - 2)} disabled={(queryParams.page ?? 1) <= 1}>
                        <IconSystem.chevronLeft size={16} />
                      </button>
                      <span style={{ padding: '0 0.5rem', fontSize: '0.85rem' }}>
                        Page {queryParams.page ?? 1} of {Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}
                      </span>
                      <button className="page-btn" onClick={() => handlePageChange(queryParams.page ?? 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                      </button>
                      <button className="page-btn" onClick={() => handlePageChange(Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25))) - 1)} disabled={(queryParams.page ?? 1) >= Math.max(1, Math.ceil(pagination.totalItems / (queryParams.pageSize ?? 25)))}>
                        <IconSystem.chevronRight size={16} />
                        <IconSystem.chevronRight size={16} style={{ marginLeft: '-8px' }} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <IconSystem.filter size={14} />
              <h3 className="search-panel-title">Filters</h3>
            </div>
            <button
              onClick={() => { handleClearFilters(); setFilterOpen(false); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', alignItems: 'center', color: 'var(--text-muted)', borderRadius: '0.25rem' }}
              title="Close filters"
            >
              <IconSystem.x size={16} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Advocate Code</label>
              <input
                type="text"
                className="form-input"
                value={advocateCodeFilter}
                onChange={(e) => setAdvocateCodeFilter(e.target.value)}
                placeholder="Filter by code..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Advocate Name</label>
              <input
                type="text"
                className="form-input"
                value={advocateNameFilter}
                onChange={(e) => setAdvocateNameFilter(e.target.value)}
                placeholder="Filter by name..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Bar Council Reg #</label>
              <input
                type="text"
                className="form-input"
                value={barRegFilter}
                onChange={(e) => setBarRegFilter(e.target.value)}
                placeholder="Filter by bar reg#..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Specialisation</label>
              <input
                type="text"
                className="form-input"
                value={specFilter}
                onChange={(e) => setSpecFilter(e.target.value)}
                placeholder="Filter by specialisation..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>City</label>
              <input
                type="text"
                className="form-input"
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                placeholder="Filter by city..."
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Country"
                options={[{ value: '', label: 'All countries' }, ...countryFilterOptions]}
                value={countryIdFilter}
                onChange={(v) => handleFilterCountryChange((v as string) || '')}
                isClearable={false}
                size="sm"
                placeholder="All countries"
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="State / Province"
                options={[{ value: '', label: 'All states' }, ...stateFilterOptions]}
                value={stateProvinceIdFilter}
                onChange={(v) => setStateProvinceIdFilter((v as string) || '')}
                isClearable={false}
                size="sm"
                isDisabled={!countryIdFilter}
                placeholder={countryIdFilter ? 'All states' : 'Select country first'}
              />
            </div>

            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'Active only (default)' },
                  { value: 'INACTIVE', label: 'Inactive only' },
                  { value: 'DELETED', label: 'Deleted only' },
                  { value: 'ALL', label: 'All (incl. deleted)' },
                ]}
                value={statusFilter}
                onChange={(v) => setStatusFilter((v as string) || '')}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      <ConfirmDialog
        open={deleteModalOpen}
        title="Confirm Delete"
        message={
          advToDelete
            ? `Are you sure you want to delete advocate "${advToDelete.advocateCode}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting..."
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setAdvToDelete(null);
          }
        }}
      />

      <AdvocateFormModal
        isOpen={formModalOpen}
        mode={formMode}
        record={selectedAdv}
        onSaved={handleSaved}
        onClose={() => setFormModalOpen(false)}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />
    </div>
  );
};

export default AdvocateMasterScreen;
